import { formatLargeNumber } from "../Numbers";

/**
 * Utility functions for currency formatting
 */

/**
 * Format a number as currency with dollar sign and two decimal places
 *
 * @param amount - The monetary amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | undefined | null,
  options: {
    currency?: string;
    locale?: string;
    decimals?: number;
    useSimpleFormat?: boolean;
  } = {}
): string => {
  // Default values
  const {
    currency = "USD",
    locale = "en-US",
    decimals = 2,
    useSimpleFormat = false,
  } = options;

  // Handle undefined/null values
  if (amount === undefined || amount === null) {
    return "$0.00";
  }

  // Use the simple format if specified (uses formatLargeNumber)
  if (useSimpleFormat) {
    return `$${formatLargeNumber(Number(amount), decimals)}`;
  }

  // Format the currency using Intl.NumberFormat
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

/**
 * Format a number as compact currency (e.g., $1.2K, $1.5M)
 * Useful for dashboard summaries and charts
 *
 * @param amount - The monetary amount to format
 * @param options - Formatting options
 * @returns Formatted compact currency string
 */
export const formatCompactCurrency = (
  amount: number | undefined | null,
  options: {
    currency?: string;
    locale?: string;
    decimals?: number;
    useSimpleFormat?: boolean;
  } = {}
): string => {
  // Default values
  const {
    currency = "USD",
    locale = "en-US",
    decimals = 1,
    useSimpleFormat = false,
  } = options;

  // Handle undefined/null values
  if (amount === undefined || amount === null) {
    return "$0";
  }

  // Use the simple format if specified (uses formatLargeNumber)
  if (useSimpleFormat) {
    return `$${formatLargeNumber(Number(amount), decimals)}`;
  }

  // Format as compact currency
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    notation: "compact",
    compactDisplay: "short",
  }).format(amount);
};

/**
 * Format a percentage value with a % sign
 *
 * @param value - The percentage value to format
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number | undefined | null,
  decimals: number = 1
): string => {
  if (value === undefined || value === null) {
    return "0%";
  }

  return `${value.toFixed(decimals)}%`;
};

/**
 * Format a monetary difference with + or - sign
 *
 * @param amount - The monetary difference to format
 * @param options - Formatting options
 * @returns Formatted currency difference string with sign
 */
export const formatCurrencyDifference = (
  amount: number | undefined | null,
  options: {
    currency?: string;
    locale?: string;
    decimals?: number;
    useSimpleFormat?: boolean;
  } = {}
): string => {
  if (amount === undefined || amount === null) {
    return "+$0.00";
  }

  const sign = amount >= 0 ? "+" : "";
  return sign + formatCurrency(amount, options);
};
